import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import AdminModal from '../common/AdminModal';
import FormInput from '../../../shared/components/FormInput';

const EMPTY_FORM = {
  slug: '',
  title: '',
};

const SLUG_RE = /^[a-z0-9_]+(?:-[a-z0-9_]+)*$/;

function slugify(value) {
  return String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9_\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

export default function NewFormModal({ show, onClose, onCreate, creating, error }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  useEffect(() => {
    if (show) {
      setForm(EMPTY_FORM);
      setSlugManuallyEdited(false);
    }
  }, [show]);

  const slug = form.slug.trim();
  const title = form.title.trim();
  const slugValid = SLUG_RE.test(slug);
  const canCreate = slugValid && title.length > 0 && !creating;

  const slugHelp = useMemo(() => {
    if (!slug.length) return 'Use lowercase letters, numbers, underscores, and dashes.';
    if (slugValid) return 'Looks good.';
    return 'Invalid slug format.';
  }, [slug, slugValid]);

  const footer = (
    <div className="d-flex justify-content-between w-100">
      <button type="button" className="btn btn-link text-muted" onClick={onClose} disabled={creating}>
        <i className="bi bi-arrow-left me-1" /> Cancel
      </button>
      <button
        type="button"
        className="btn btn-primary"
        onClick={() => onCreate({ slug, title })}
        disabled={!canCreate}
      >
        {creating ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" />
            Creating...
          </>
        ) : (
          'Create and open editor'
        )}
      </button>
    </div>
  );

  return (
    <AdminModal show={show} onClose={onClose} title="New Form" footer={footer} size="lg">
      <FormInput
        id="new-form-title"
        label="Title"
        placeholder="e.g. Filter Inspection"
        value={form.title}
        onChange={(e) => {
          const nextTitle = e.target.value;
          setForm((prev) => ({
            ...prev,
            title: nextTitle,
            slug: slugManuallyEdited ? prev.slug : slugify(nextTitle),
          }));
        }}
        disabled={creating}
        required
      />

      <FormInput
        id="new-form-slug"
        label="Slug"
        placeholder="e.g. filter-inspection"
        value={form.slug}
        onChange={(e) => {
          setSlugManuallyEdited(true);
          setForm((prev) => ({ ...prev, slug: e.target.value }));
        }}
        disabled={creating}
        required
        error={slug.length > 0 && !slugValid ? slugHelp : undefined}
        helperText={slugHelp}
      />

      {error && (
        <div className="alert alert-danger mt-3 mb-0" role="alert">
          {error}
        </div>
      )}
    </AdminModal>
  );
}

NewFormModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  creating: PropTypes.bool,
  error: PropTypes.string,
};

NewFormModal.defaultProps = {
  creating: false,
  error: '',
};
